import { push, replace } from 'connected-react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import type * as routingActions from '~/actions/routing.actions';

import { ActionKeys } from '~/actions/constants';
import { QS_LOCAL_STORAGE_KEY } from '~/constants/local-storage-keys';
import { getLocation, getParsedQS } from '~/selectors/router.selectors';
import { readFromLocalStorage, saveInLocalStorage } from '~/utils/local-storage';
import { parseUrlSearchParams, stringifyUrlSearchParams } from '~/utils/url';

import { type ApplicationState } from '../../store';

function* changeQueryParams(action: ReturnType<typeof routingActions.changeQueryString>) {
  const location = yield select((state: ApplicationState) => state.router!.location);
  const newQueryParams = {
    ...action.payload,
  };
  const queryString = stringifyUrlSearchParams(newQueryParams);
  yield put(push(`${location.pathname}?${queryString}`));
}

function* updateQueryParam(action: ReturnType<typeof routingActions.updateQueryParam>) {
  const location = yield select((state: ApplicationState) => state.router!.location);
  const currentQueryParams = parseUrlSearchParams(location.search.substring(1));
  const newQueryParams = {
    ...{ order_by: 'desc', sort_by: '_score' },
    ...currentQueryParams,
    [action.payload.name]: action.payload.value,
  };
  yield put(push(`${location.pathname}?${stringifyUrlSearchParams(newQueryParams)}`));
  yield storeCurrentQueryParams();
}

export function* storeCurrentQueryParams() {
  const currentQS = yield select(getParsedQS);
  const filteredQS = Object.fromEntries(
    Object.entries(currentQS).filter(([key]) => !key.startsWith('_')),
  );

  yield call(saveInLocalStorage, QS_LOCAL_STORAGE_KEY, JSON.stringify(filteredQS));
}

function* restoreQueryParams() {
  const storedQSString = readFromLocalStorage(QS_LOCAL_STORAGE_KEY);
  if (storedQSString) {
    const storedQS = JSON.parse(storedQSString);

    // In case an empty object was stored
    if (Object.keys(storedQS).length > 0) {
      const location = yield select(getLocation);
      const queryString = stringifyUrlSearchParams(storedQS);

      yield put(replace(`${location.pathname}?${queryString}`));
    }
  }
}

export default [
  takeLatest(ActionKeys.CHANGE_QUERY_PARAMS, changeQueryParams),
  takeLatest(ActionKeys.UPDATE_QUERY_PARAM, updateQueryParam),
  takeLatest(ActionKeys.STORE_CURRENT_QUERY_PARAMS, storeCurrentQueryParams),
  takeLatest(ActionKeys.RESTORE_QUERY_PARAMS, restoreQueryParams),
];
